import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { commissioningQueryKey } from '@/features/commissioning/useCommissioning';

/**
 * SLICE 14c — CLEARANCE TO COMMAND (the staff-facing half).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 TWO DIFFERENT FACTS, AND THIS FILE NEVER LETS THEM MERGE.
 * ─────────────────────────────────────────────────────────────────────────────
 * `commandVerifiedAt` — HARDENED. A machine proof: `api/.../hardening.ts` writes
 *   it, and only after a credential round-tripped through the physical tracker.
 * `commandEnabledAt`  — CLEARED. A named human's attestation that they accept the
 *   risk of commanding a tracker before that proof exists, with a note saying
 *   what they did.
 *
 * The API keeps them in two columns with two writers on purpose. The UI keeps
 * them in two visual states with two words on purpose. An attestation rendered
 * as a proof is exactly how "verified" stops meaning anything.
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts` (seam S1):
 *   POST   /api/admin/devices/:deviceId/command-clearance  { note }
 *   DELETE /api/admin/devices/:deviceId/command-clearance
 *   GET    /api/admin/command-clearances
 */

/** One row of `GET /api/admin/command-clearances` — cleared, and NOT hardened.
 *  The endpoint filters hardened devices out (a proof does not need listing as an
 *  accepted risk), so every row here is a live human attestation. */
export interface CommandClearance {
  deviceId: string;
  imei: string;
  protocol: string | null;
  /** ISO instant the clearance was recorded, or null if the row is malformed. */
  commandEnabledAt: string | null;
  /**
   * 🔴 WHO accepted the risk, by NAME — the field this list exists for. An
   * operator reading a bench cannot resolve a staff uuid to a person, so a
   * clearance whose author is only an id records the "who" somewhere nobody
   * looks, which is the same as not recording it. Typed nullable because the
   * wire says so; it cannot be null today (`staff.name` is NOT NULL and the FK
   * refuses to delete a referenced row), and the API's integration suite asserts
   * both of those schema facts so this stops being decorative if either relaxes.
   */
  commandEnabledBy: string | null;
  commandEnabledNote: string | null;
}

export interface CommandClearanceList {
  clearances: CommandClearance[];
}

/** The POST response — carries BOTH facts, so the caller can never read the
 *  attestation as a proof. `commandVerifiedAt` is null unless the device is also
 *  hardened; the two are independent. */
export interface ClearResult {
  deviceId: string;
  imei: string;
  protocol: string | null;
  commandVerifiedAt: string | null;
  commandEnabledAt: string | null;
  commandEnabledNote: string | null;
}

/** The DELETE response. `commandVerifiedAt` is the WHOLE point: withdrawing a
 *  human's attestation must not withdraw a machine proof, so the caller reads
 *  this to say whether commanding actually stopped. */
export interface WithdrawResult {
  deviceId: string;
  commandVerifiedAt: string | null;
  commandEnabledAt: null;
}

/** A 4xx is a decision, not a hiccup — never retried. */
const retry4xxNever = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2;

export const clearancesQueryKey = ['admin', 'command-clearances'] as const;
export const candidatesQueryKey = ['admin', 'command-clearances', 'candidates'] as const;

/**
 * SLICE 14d — a device that COULD be cleared: a command channel, no clearance.
 * The complement of `CommandClearance`, so the picker offers exactly the set the
 * write path accepts and nothing it would 409. Mirrors `ClearanceCandidateZ`.
 */
export interface ClearanceCandidate {
  deviceId: string;
  imei: string;
  /** The literal set `clearDeviceForCommanding` accepts — a picker must not
   *  offer a protocol the write path rejects. */
  protocol: 'teltonika' | 'gt06';
  /** null means warehouse stock — the picker labels it, never a blank. */
  plate: string | null;
  tenantName: string | null;
  commandVerifiedAt: string | null;
}

export interface ClearanceCandidateList {
  candidates: ClearanceCandidate[];
}

/**
 * The clearable-devices list, feeding the "Clear a device" picker. Super-admin
 * on the server (it carries IMEIs), so the query is gated by `enabled` rather
 * than fired-and-403'd — the same pattern `useCommandClearances` uses.
 */
export function useClearanceCandidates(enabled = true): UseQueryResult<ClearanceCandidateList> {
  return useQuery({
    queryKey: candidatesQueryKey,
    enabled,
    queryFn: ({ signal }) =>
      apiJson<ClearanceCandidateList>('/api/admin/command-clearances/candidates', { signal }),
    retry: retry4xxNever,
  });
}

/**
 * The cleared-but-not-hardened list. `requireSuperAdmin` on the server, so the
 * query is gated by `enabled` rather than fired-and-403'd for an `admin_staff`.
 */
export function useCommandClearances(enabled = true): UseQueryResult<CommandClearanceList> {
  return useQuery({
    queryKey: clearancesQueryKey,
    enabled,
    queryFn: ({ signal }) =>
      apiJson<CommandClearanceList>('/api/admin/command-clearances', { signal }),
    retry: retry4xxNever,
  });
}

/**
 * The failures the clear dialog turns into a sentence a person can act on.
 *
 * `note_required` (422) is the one that matters: the note is the whole worth of
 * the record, and the server refuses a short one. `no_command_channel` (409) is
 * an app-based tracker that can never be commanded whatever anyone attests.
 */
export type ClearFailure = 'note_required' | 'no_command_channel' | 'not_found' | 'unknown';

export function classifyClearError(error: unknown): ClearFailure {
  if (error instanceof ApiError) {
    if (error.status === 422) return 'note_required';
    if (error.status === 409) return 'no_command_channel';
    if (error.status === 404) return 'not_found';
  }
  return 'unknown';
}

/**
 * Record a clearance. Invalidates BOTH the clearance list and the commissioning
 * bench, because the same fact is read on two screens: the bench shows a device's
 * clearance state inline, and this list enumerates every cleared-not-hardened
 * device. Refreshing one and not the other would leave them disagreeing about
 * whether a device a person just cleared is cleared.
 */
export function useClearDevice(): UseMutationResult<
  ClearResult,
  Error,
  { deviceId: string; note: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, note }) =>
      apiJson<ClearResult>(
        `/api/admin/devices/${encodeURIComponent(deviceId)}/command-clearance`,
        { method: 'POST', body: JSON.stringify({ note }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clearancesQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
      // Slice 14d: the device just left the candidates set (it now has a
      // clearance) and joined the clearances list, so the picker must stop
      // offering it.
      //
      // 🔴 THIS LINE IS REDUNDANT TODAY AND SAYS SO RATHER THAN CLAIMING WORK IT
      // DOES NOT DO. `candidatesQueryKey` is `[...clearancesQueryKey,
      // 'candidates']` and TanStack invalidates by PREFIX, so the call above
      // already refreshes it. It is kept as the defence against a future key
      // restructure that breaks that prefix relationship — a silent breakage,
      // because the picker would simply keep showing a stale list with nothing
      // erroring. Naming the redundancy is the point: a reader who thinks this
      // is load-bearing will not delete it, and a reader who thinks it is doing
      // the refreshing will not notice when the prefix stops holding.
      void qc.invalidateQueries({ queryKey: candidatesQueryKey });
    },
  });
}

/**
 * Withdraw a clearance. Same dual invalidation as clearing.
 *
 * The caller must read `commandVerifiedAt` off the result: a hardened device
 * stays commandable, and telling the operator "commanding has stopped" when it
 * has not would be worse than saying nothing.
 */
export function useWithdrawClearance(): UseMutationResult<WithdrawResult, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      apiJson<WithdrawResult>(
        `/api/admin/devices/${encodeURIComponent(deviceId)}/command-clearance`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clearancesQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
      // A withdrawn, un-hardened device is command-capable and un-cleared again —
      // a candidate once more, so the picker must pick it back up. Redundant via
      // the prefix, kept for the same reason as in `useClearDevice` above.
      void qc.invalidateQueries({ queryKey: candidatesQueryKey });
    },
  });
}
